import {useEffect, useRef, useState} from 'react';
import {createAction} from '@packages/utilities/create-action';
import {
  SLIDER_ANIMATED,
  SLIDER_SLIDE_SELECTED,
} from '@packages/systems/designer/actions';
import {
  getName,
  getPluginName,
  namesEqual,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {
  Component,
  ExpressionPath,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {
  updateExpressionAction,
  useExpressionEditorContext,
} from '@packages/systems/ExpressionEditor-utils';
import {getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {
  EElement,
  EEnum,
  EList,
  ERecord,
  EText,
  type ExpressionElement,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {generateGuid} from '@packages/utilities/guid';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {SLIDES_PATH, DURATION_PATH} from '../constants';
import {selectSelectedNativeIdPath} from '@packages/utilities/platform-selectors';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';
import {type ExpressionEditorDispatch} from '@packages/systems/ExpressionEditor-utils';
import {getElementWithAncestors} from '@packages/utilities/platform';
import {getActiveDocument} from '@packages/systems/core/helpers/SiteIframeMigrationHelper';
import {getNativeNodeByNativeIdPath} from '@packages/systems/core/utils/NativeIdUtils';
export const sliderSlideSelected = createAction<
  typeof SLIDER_SLIDE_SELECTED,
  {slideId: number | string}
>(SLIDER_SLIDE_SELECTED);
const sliderAnimated = createAction<
  typeof SLIDER_ANIMATED,
  {
    index: number | typeof NEXT | typeof PREV;
  }
>(SLIDER_ANIMATED);

export type Slide = {
  id: string;
  label: string;
  index: number;
};

const NEXT = 'next';
const PREV = 'prev';

const slideElement = (id: string) =>
  getSecond(
    instantiateExpressionWithUniqueIds(
      EElement({
        id: 'Slide',
        type: qualify(Plugins.name('Slider'), Component.name('SliderSlide')),
        data: ERecord({
          tag: EEnum('div'),
          slider: ERecord({
            type: EText('slide'),
          }),
          children: EList([]),
        }),
      })
    )(id)
  );

const appendSlideToExpr = (expr: Expression, newSlide: Expression) =>
  Expressions.updateIn(expr, SLIDES_PATH, (list) => {
    const slidesList = Expressions.getList(list);
    if (!slidesList) throw Error('failed to getList from `slidesList`');
    return EList([...slidesList, newSlide]);
  });

const sliderSlideType = qualify(
  Plugins.name('Slider'),
  typeName('SliderSlide')
);
const getSlideIdsFromList = (expr: Expression) => {
  const list = Expressions.getList(expr);
  if (list === null) {
    return [];
  }
  return list.reduce<Array<string>>((acc, expression) => {
    const element: ExpressionElement | null =
      Expressions.getElement(expression);
    if (element === null) {
      return acc;
    }
    const elementName = element ? getName(element.type) : null;
    const elementPluginName = element ? getPluginName(element.type) : null;

    if (!elementName || !elementPluginName) return acc;

    const elementType = qualify(
      Plugins.name(elementPluginName),
      Component.name(elementName)
    );

    if (namesEqual(elementType, sliderSlideType)) {
      acc.push(element.id);
    }
    return acc;
  }, []);
};

const getSlides = (expr: Expression, path: ExpressionPath) => {
  const slidesExpr = Expressions.getIn(expr, path);
  return slidesExpr ? getSlideIdsFromList(slidesExpr) : [];
};

const isSlide = (id: string, _slides: Array<Slide>) =>
  _slides.find((slide) => slide.id === id);

const getDuration = (expr: Expression): number => {
  const durationExpr = Expressions.getIn(expr, DURATION_PATH);
  if (durationExpr == null)
    throw Error('Unable to get expression at DURATION_PATH');
  return Expressions.getNumber(durationExpr) || 0;
};

/**
 * Returns the current slide ID based on context.
 *
 * Mini toolbar: Returns miniToolbarCurrentSlideId (React state). When in edit mode,
 * the currently selected element is always resolved to the slider wrapper element,
 * so we use the expression data to determine the current slide and update the React state.
 *
 * Element Settings: Derives from canvas selection. We can't use miniToolbarCurrentSlideId
 * here because users can select slide children directly in the canvas (not through slider
 * controls). Element Settings must reflect those external selections, which only appear
 * in the selection ancestry.
 */
const getCurrentSlideId = (
  isCompactContext: boolean,
  miniToolbarCurrentSlideId: string,
  selectedNodeAncestorIds: Set<string>,
  slides: Array<Slide>
): string => {
  if (isCompactContext) {
    return miniToolbarCurrentSlideId;
  }
  const currentSlideIndex = slides.findIndex((s) =>
    selectedNodeAncestorIds.has(s.id)
  );
  return currentSlideIndex !== -1 ? (slides[currentSlideIndex]?.id ?? '') : '';
};

/**
 * Manages slider navigation with dual-context support.
 *
 * Mini toolbar: Users click the wrapper element. Current slide tracked in React state
 * since no slide child is selected in the canvas.
 *
 * Element Settings: Users select individual slide children. Current slide derived from
 * which slide appears in the canvas selection's ancestor chain.
 */
const useSliderControls = (
  expr: ContextualExpression,
  dispatch: ExpressionEditorDispatch,
  options?: {suppressMountAnimation?: boolean}
) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  // Suppresses the initial slide animation on mount when replaying a pending
  // mini toolbar action. Without this, focusing into a component instance would
  // trigger an unwanted "snap to first slide" animation before the intended
  // slide navigation is replayed.
  const suppressMountAnimationRef = useRef(
    options?.suppressMountAnimation ?? false
  );
  const eeContext = useExpressionEditorContext();
  const isCompactContext = eeContext === 'compact';

  const selectedNativeIdPath = useDesignerSelector(selectSelectedNativeIdPath);
  const selectedNativeId =
    selectedNativeIdPath[selectedNativeIdPath.length - 1] ?? '';

  const fluxDispatch = useDispatch();
  const [currentTransitioningSlide, setCurrentTransitionSlide] = useState<
    string | null
  >(null);
  const strippedExpr = stripAll(expr);

  const duration = getDuration(strippedExpr);

  const slideIds = getSlides(strippedExpr, SLIDES_PATH);

  const slides: Array<Slide> = slideIds.map((id, index) => ({
    id,
    label: `slide ${index + 1}`,
    index,
  }));

  useEffect(() => {
    return () => {
      timerRef?.current && clearTimeout(timerRef.current);
    };
  }, []);

  /**
   * Mini toolbar tracks current slide in React state.
   *
   * Users click the wrapper element in the mini toolbar, not individual slide children.
   * With no slide child selected in the canvas, we can't derive the current slide from
   * canvas selection like Element Settings does.
   *
   * Defaults to first slide. Clicking away from the slider and returning resets to the
   * first slide, matching Element Settings behavior.
   */
  const [miniToolbarCurrentSlideId, setMiniToolbarCurrentSlideId] =
    useState<string>(slideIds[0] ?? '');

  /**
   * Build ancestor chain by traversing the expression tree, not just the selection path.
   *
   * `selectedNativeIdPath` from the Flux store only contains the direct selection path,
   * which may not include all semantic ancestors needed to determine which slide is active.
   * `getElementWithAncestors` actively walks up the tree from the selected element,
   * ensuring we have the complete ancestor chain. This prevents the useEffect below from
   * incorrectly resetting to the first slide when no slide IDs are found in the path.
   */
  const selectedNodeAncestorIds = getElementWithAncestors(strippedExpr)(
    selectedNativeId
  ).reduce((acc, n) => acc.add(n.id), new Set<string>());

  const getIndex = () => {
    const i = slides.findIndex((s) => s.id === currentTransitioningSlide);
    if (i !== -1) return i;

    return slides.findIndex((s) =>
      isCompactContext
        ? s.id === miniToolbarCurrentSlideId
        : selectedNodeAncestorIds.has(s.id)
    );
  };

  const getNext = () => {
    const i = getIndex();
    if (i === -1) return slides[1];
    return slides[i + 1] || slides[0];
  };

  const getPrev = () => {
    const i = getIndex();
    return slides[i - 1] || slides[slides.length - 1];
  };

  const triggerAnimation = (index: number | typeof NEXT | typeof PREV) => {
    fluxDispatch(sliderAnimated({index}));
  };

  const updateSelectedNodeNativeId = (slideId: number | string) =>
    fluxDispatch(sliderSlideSelected({slideId}));

  /**
   * Persists slide selection according to the current context.
   * Mini toolbar: Updates React state. Element Settings: Updates canvas selection.
   */
  const persistSlideSelection = (id: string) => {
    if (!isCompactContext) {
      /**
       * In edit mode, the selected node is the slide wrapper.
       * running this function navigates us away from the slide wrapper and into the slide content.
       */
      updateSelectedNodeNativeId(id);
    }
    setMiniToolbarCurrentSlideId(id);
  };

  /**
   * Current slide determination differs by context:
   * - Mini toolbar: Read from React state (resets on unmount)
   * - Element Settings: Derive from canvas selection (which slide child is selected)
   */
  const currentSlideId = getCurrentSlideId(
    isCompactContext,
    miniToolbarCurrentSlideId,
    selectedNodeAncestorIds,
    slides
  );

  const addSlide = () => {
    const id = generateGuid();
    const sliderWithNewSlide = appendSlideToExpr(
      strippedExpr,
      slideElement(id)
    );
    dispatch(updateExpressionAction(sliderWithNewSlide));
    updateSelectedNodeNativeId(id);
  };

  const immediatelyToSlide = (id: string) => {
    const index = slides.findIndex((slide) => id === slide.id);
    if (index === -1) throw Error(`couldn't find a slide with id ${id}`);
    triggerAnimation(index);
    persistSlideSelection(id);
  };

  const transitionToSlide = (
    next: () => Slide | undefined,
    direction: typeof NEXT | typeof PREV
  ) => {
    const slide = next();
    if (slide) {
      setCurrentTransitionSlide(slide.id);
      setMiniToolbarCurrentSlideId(slide.id);
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    triggerAnimation(direction);
    timerRef.current = setTimeout(() => {
      setCurrentTransitionSlide(null);
    }, duration);
  };

  /**
   * Triggers animation to the first slide on mount.
   *
   * The slider in the site iframe is jQuery-based and doesn't observe Flux state directly.
   * It relies on SLIDER_ANIMATED actions processed by WidgetBridge to display slides.
   * These useEffect hooks exist because there's no direct way to communicate with the
   * site iframe without going through this bridge pattern.
   *
   * Mini toolbar: Always animates to first slide. miniToolbarCurrentSlideId resets on mount, but
   * we still dispatch the action to trigger the visual transition.
   *
   * Element Settings: Shows first slide only if no slide child is currently selected in canvas.
   *
   * The dependency array historically omitted selectedNodeAncestorIds, slides, and
   * triggerAnimation. It's unclear if this was deliberate. Adding these could cause
   * regressions, so the current behavior is preserved.
   */
  useEffect(() => {
    if (suppressMountAnimationRef.current) {
      suppressMountAnimationRef.current = false;
      return;
    }
    if (
      slides.length > 0 &&
      Array.from(selectedNodeAncestorIds).every((id) => !isSlide(id, slides))
    ) {
      triggerAnimation(0);
    }
  }, [selectedNativeId, strippedExpr, isCompactContext]);

  /**
   * Element Settings only: Syncs slider animation when canvas selection changes.
   *
   * When a slide child is selected in the canvas (outside the slider controls),
   * dispatch SLIDER_ANIMATED to show that slide visually.
   *
   * The dependency array historically omitted selectedNodeAncestorIds, slides, and
   * triggerAnimation. It's unclear if this was deliberate. Adding these could cause
   * regressions, so the current behavior is preserved.
   */
  useEffect(() => {
    if (isCompactContext) return;
    const foundSlide = slides.find((s) => selectedNodeAncestorIds.has(s.id));
    foundSlide && triggerAnimation(foundSlide.index);
  }, [selectedNativeId, isCompactContext]);

  /**
   * Syncs miniToolbarCurrentSlideId when the slider widget in the site iframe
   * changes slides (e.g. the user clicks a slide-nav bubble on the canvas).
   *
   * The jQuery slider widget dispatches a bubbling `wf-slider-change`
   * CustomEvent on the slider wrapper whenever `change()` runs in designer
   * mode. We listen on the site iframe document and match the event to our
   * slider via DOM relationship rather than id-string compare — the DOM
   * stores raw guids in `data-w-id`, while selectedNativeIdPath stores
   * `<guid>_instance<N>` for elements inside component instances, so direct
   * string compare misses them. `getNativeNodeByNativeIdPath` already
   * handles that suffix dissection.
   */
  const slidesRef = useRef(slides);
  slidesRef.current = slides;

  useEffect(() => {
    if (!isCompactContext) return;

    const iframeDoc = getActiveDocument();
    if (!iframeDoc) return;

    const findSelectedSliderEl = (): HTMLElement | null => {
      const selectedEl = getNativeNodeByNativeIdPath(selectedNativeIdPath);
      if (!selectedEl) return null;
      return selectedEl.matches?.('.w-slider')
        ? selectedEl
        : selectedEl.querySelector<HTMLElement>('.w-slider');
    };

    const setSlideByIndex = (index: number) => {
      const slide = slidesRef.current[index];
      if (slide) setMiniToolbarCurrentSlideId(slide.id);
    };

    const handleSliderChange = (evt: Event) => {
      const target = evt.target as HTMLElement | null;
      const sliderEl = findSelectedSliderEl();
      if (!target || !sliderEl || !sliderEl.contains(target)) return;
      setSlideByIndex((evt as CustomEvent<{index: number}>).detail.index);
    };

    iframeDoc.addEventListener('wf-slider-change', handleSliderChange);

    // Sync initial state: find the active nav dot on our slider so
    // re-selecting it preserves the visible slide instead of resetting to 1.
    const sliderEl = findSelectedSliderEl();
    const nav = sliderEl?.querySelector('.w-slider-nav');
    const activeDot = nav?.querySelector(':scope > .w-active');
    if (nav && activeDot) {
      setSlideByIndex(Array.from(nav.children).indexOf(activeDot));
    }

    return () => {
      iframeDoc.removeEventListener('wf-slider-change', handleSliderChange);
    };
  }, [isCompactContext, selectedNativeIdPath, setMiniToolbarCurrentSlideId]);

  const toPrevSlide = () => transitionToSlide(getPrev, PREV);
  const toNextSlide = () => transitionToSlide(getNext, NEXT);

  return {
    toPrevSlide,
    toNextSlide,
    immediatelyToSlide,
    addSlide,
    slides,
    currentSlideId,
  };
};

export default useSliderControls;
